import { useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/cjs/Page/TextLayer.css';
import 'react-pdf/dist/cjs/Page/AnnotationLayer.css';
import './agreementPdfWorker';

export default function AgreementComponent({ agreementType, documentUrl, onAccept, onCancel }) {
    const [pageCount, setPageCount] = useState(0);
    const [loadedPages, setLoadedPages] = useState(0);
    const [hasReachedEnd, setHasReachedEnd] = useState(false);
    const [isAccepted, setIsAccepted] = useState(false);
    const [loadError, setLoadError] = useState('');
    const renderedPages = useRef(new Set());
    const accepted = useRef(false);
    const viewerRef = useRef(null);
    const validDocumentUrl = getValidDocumentUrl(documentUrl);

    const markReachedEnd = () => {
        const viewer = viewerRef.current;
        if (
            pageCount > 0 &&
            loadedPages === pageCount &&
            !loadError &&
            viewer &&
            viewer.scrollTop + viewer.clientHeight >= viewer.scrollHeight - 1
        ) {
            setHasReachedEnd(true);
        }
    };

    const handleWheel = (event) => {
        if (event.deltaY > 0) markReachedEnd();
    };

    const handlePageRenderSuccess = (pageNumber) => {
        renderedPages.current.add(pageNumber);
        setLoadedPages(renderedPages.current.size);
    };

    const handleAccept = () => {
        if (!hasReachedEnd || !isAccepted || !validDocumentUrl || accepted.current) return;

        accepted.current = true;
        onAccept({
            accepted: true,
            acceptedDate: new Date().toISOString(),
            acceptedAgreement: validDocumentUrl
        });
    };

    return (
        <Modal show onHide={onCancel} keyboard centered size="xl">
            <Modal.Header closeButton className="border-b border-gray-200">
                <Modal.Title>{agreementType} Agreement</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <div
                    ref={viewerRef}
                    onScroll={markReachedEnd}
                    onWheel={handleWheel}
                    aria-label={`${agreementType} agreement document`}
                    className="max-h-[65vh] overflow-auto rounded border border-gray-300 bg-gray-100 p-3"
                >
                    {!validDocumentUrl ? (
                        <p role="alert" className="text-sm text-red-600">The agreement document URL is invalid.</p>
                    ) : loadError ? (
                        <p role="alert" className="text-sm text-red-600">{loadError}</p>
                    ) : (
                        <Document
                            file={validDocumentUrl}
                            loading={<p className="py-8 text-center text-gray-600">Loading agreement document...</p>}
                            error={<p role="alert" className="py-8 text-center text-red-600">Unable to load the agreement document.</p>}
                            onLoadSuccess={({ numPages }) => {
                                setPageCount(numPages);
                                setLoadedPages(0);
                                renderedPages.current.clear();
                            }}
                            onLoadError={() => setLoadError('Unable to load the agreement document.')}
                        >
                            {Array.from(new Array(pageCount), (_, index) => (
                                <Page
                                    key={`agreement-page-${index + 1}`}
                                    pageNumber={index + 1}
                                    width={760}
                                    onRenderSuccess={() => handlePageRenderSuccess(index + 1)}
                                    onRenderError={() => setLoadError('Unable to render the agreement document.')}
                                    className="mb-3"
                                />
                            ))}
                        </Document>
                    )}
                </div>
                <label className="mt-4 flex items-center gap-2 text-sm text-gray-800">
                    <input
                        type="checkbox"
                        checked={isAccepted}
                        disabled={!hasReachedEnd || !validDocumentUrl || Boolean(loadError)}
                        onChange={(event) => setIsAccepted(event.target.checked)}
                    />
                    I have read and accept the {agreementType.toLowerCase()} agreement.
                </label>
            </Modal.Body>
            <Modal.Footer className="border-t border-gray-200">
                <button
                    type="button"
                    onClick={onCancel}
                    className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleAccept}
                    disabled={!hasReachedEnd || !isAccepted || Boolean(loadError)}
                    className="rounded bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Accept
                </button>
            </Modal.Footer>
        </Modal>
    );
}

function getValidDocumentUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return '';

    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:' ? value : '';
    } catch {
        return '';
    }
}
